import { apiListUserProfile } from "./api/master";

// Dynamic theme manager for Sidebar AND "Save", "Submit", "Add New" buttons
export const DEFAULT_THEME_BTN_COLOR = "#4263eb";
export const THEME_BTN_STORAGE_KEY = "custom_btn_theme_color";

export const PRESET_THEME_COLORS = [
  { name: "Royal Blue", value: "#4263eb", isDefault: true },
  { name: "Emerald Green", value: "#10b981" },
  { name: "Electric Violet", value: "#7c3aed" },
  { name: "Crimson Red", value: "#ef4444" },
  { name: "Sunset Orange", value: "#f97316" },
  { name: "Teal Cyan", value: "#0d9488" },
  { name: "Classic Indigo", value: "#3734a9" },
  { name: "Midnight Slate", value: "#1e293b" },
  { name: "Hot Pink", value: "#db2777" },
  { name: "Warm Amber", value: "#d97706" },
  { name: "Golden Yellow", value: "#d4af37" },
];

/**
 * Adjust brightness of a hex color
 */
export function adjustBrightness(hex, percent) {
  if (!hex || typeof hex !== "string" || !hex.startsWith("#")) return hex;
  let c = hex.replace("#", "").trim();
  if (c.length === 3) c = c.split("").map((x) => x + x).join("");
  const num = parseInt(c, 16);
  if (isNaN(num)) return hex;

  const r = (num >> 16) & 255;
  const g = (num >> 8) & 255;
  const b = num & 255;

  const adjust = (val) =>
    Math.min(255, Math.max(0, Math.round(val + (val * percent) / 100)));
  const toHex = (n) => n.toString(16).padStart(2, "0");

  return `#${toHex(adjust(r))}${toHex(adjust(g))}${toHex(adjust(b))}`;
}

/**
 * Generate a modern 135deg linear gradient for the sidebar based on any color
 */
export function getSidebarGradient(hex) {
  if (!hex) return "linear-gradient(135deg, #868cff 0%, #4318ff 100%)";

  const presetGradients = {
    "#4263eb": "linear-gradient(135deg, #868cff 0%, #4318ff 100%)",
    "#10b981": "linear-gradient(135deg, #34d399 0%, #065f46 100%)",
    "#7c3aed": "linear-gradient(135deg, #a78bfa 0%, #5b21b6 100%)",
    "#ef4444": "linear-gradient(135deg, #f87171 0%, #991b1b 100%)",
    "#f97316": "linear-gradient(135deg, #fb923c 0%, #9a3412 100%)",
    "#0d9488": "linear-gradient(135deg, #2dd4bf 0%, #115e59 100%)",
    "#3734a9": "linear-gradient(135deg, #6366f1 0%, #312e81 100%)",
    "#1e293b": "linear-gradient(135deg, #475569 0%, #0f172a 100%)",
    "#db2777": "linear-gradient(135deg, #f472b6 0%, #9d174d 100%)",
    "#d97706": "linear-gradient(135deg, #fbbf24 0%, #78350f 100%)",
    "#d4af37": "linear-gradient(135deg, #fef08a 0%, #b45309 100%)",
  };

  const lower = hex.toLowerCase();
  if (presetGradients[lower]) {
    return presetGradients[lower];
  }

  const light = adjustBrightness(hex, 40);
  const dark = adjustBrightness(hex, -25);
  return `linear-gradient(135deg, ${light} 0%, ${dark} 100%)`;
}

/**
 * Get current saved theme color from localStorage (survives logout/login)
 */
export function getSavedThemeBtnColor() {
  try {
    const saved = localStorage.getItem(THEME_BTN_STORAGE_KEY);
    if (saved && /^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/.test(saved)) {
      return saved;
    }
  } catch (e) {
    console.error("Error reading theme color:", e);
  }
  return DEFAULT_THEME_BTN_COLOR;
}

/**
 * Save theme color to localStorage and apply immediately to buttons AND sidebar
 */
export function saveThemeBtnColor(color) {
  try {
    localStorage.setItem(THEME_BTN_STORAGE_KEY, color);
  } catch (e) {
    console.error("Error saving theme color to localStorage:", e);
  }
  applyThemeBtnColor(color);
}

/**
 * Reset theme color to default
 */
export function resetThemeBtnColor() {
  try {
    localStorage.removeItem(THEME_BTN_STORAGE_KEY);
  } catch (e) {
    console.error("Error resetting theme color:", e);
  }
  applyThemeBtnColor(DEFAULT_THEME_BTN_COLOR);
}

/**
 * Determine if a DOM element is a "Save", "Submit", or "Add New" button
 * Specifically excluding filter, search, reset, clear, export, cancel, logout, etc.
 */
export function isTargetButton(el) {
  if (!el || !(el instanceof Element)) return false;

  const tagName = el.tagName.toUpperCase();
  if (tagName !== "BUTTON" && tagName !== "A" && tagName !== "INPUT") {
    return false;
  }
  if (tagName === "INPUT" && el.type !== "submit" && el.type !== "button") {
    return false;
  }

  // Do NOT touch sidebar, navbar, header, pagination, or sweetalert modals
  if (
    el.closest("#sidebar") ||
    el.closest(".sidebar") ||
    el.closest("#navbar") ||
    el.closest(".navbar") ||
    el.closest("#header") ||
    el.closest(".pagination") ||
    el.closest(".swal2-container")
  ) {
    return false;
  }

  // Check excluded classes (secondary, outline, close buttons)
  const className = (el.className || "").toString().toLowerCase();
  if (
    className.includes("btn-secondary") ||
    className.includes("btn-outline-secondary") ||
    className.includes("btn-close") ||
    className.includes("close")
  ) {
    return false;
  }

  // Master add forms "Add New" button
  if (el.id === "createNew") {
    return true;
  }

  // All module filter buttons, reports buttons, and primary action buttons (btn-primary)
  if (className.includes("btn-primary")) {
    return true;
  }

  // Logout confirmation button in modal
  if (
    el.id === "sidebar-logout-confirm-btn" ||
    (el.closest(".modal") && (el.innerText || el.textContent || "").trim().toLowerCase() === "logout")
  ) {
    return true;
  }

  // Extract clean text
  const rawText = (el.innerText || el.textContent || el.value || "").trim().toLowerCase();
  const cleanText = rawText.replace(/\s+/g, " ");

  // Explicitly excluded action keywords
  const excludedKeywords = [
    "cancel",
    "search",
    "filter",
    "reset",
    "clear",
    "clear filters",
    "export",
    "download",
    "download excel",
    "freeze",
    "unfreeze",
    "print",
    "share",
    "upload",
    "upload excel",
    "excel",
    "logout",
    "login",
    "sign in",
    "back",
    "back to list",
    "no",
    "yes",
    "close",
    "ok",
    "apply",
    "view",
    "edit",
  ];

  if (excludedKeywords.some((kw) => cleanText === kw || cleanText.startsWith(kw + " "))) {
    return false;
  }

  // Match "Save" or "Submit" actions
  if (
    cleanText === "save" ||
    cleanText === "submit" ||
    cleanText === "save changes" ||
    cleanText === "save & next" ||
    cleanText === "save and next" ||
    cleanText === "update" ||
    cleanText === "submit voucher" ||
    cleanText.endsWith(" save") ||
    cleanText.endsWith(" submit")
  ) {
    return true;
  }

  // Match "Add New" / "New ..." / "Create New ..." / "Add ..."
  if (
    cleanText === "add new" ||
    cleanText.startsWith("new ") ||
    cleanText.startsWith("create new") ||
    cleanText.startsWith("add ")
  ) {
    return true;
  }

  // Header Add links that navigate to /add route with btn class (e.g. Stock Adjustment)
  const href = el.getAttribute("href") || "";
  if (
    tagName === "A" &&
    (href.endsWith("/add") || href.includes("/add")) &&
    className.includes("btn")
  ) {
    return true;
  }

  return false;
}

/**
 * Scan DOM and apply the theme color to matching buttons
 */
export function scanAndApplyTargetButtons(color) {
  const activeColor = color || getSavedThemeBtnColor();
  const elements = document.querySelectorAll(
    'button, a, input[type="submit"], input[type="button"]'
  );

  elements.forEach((el) => {
    if (isTargetButton(el)) {
      if (el.dataset.dynamicTheme !== "true") {
        el.dataset.dynamicTheme = "true";
      }
      el.style.setProperty("background-color", activeColor, "important");
      el.style.setProperty("background", activeColor, "important");
      el.style.setProperty("border-color", activeColor, "important");
      el.style.setProperty("color", "#ffffff", "important");
    } else if (el.dataset.dynamicTheme === "true") {
      delete el.dataset.dynamicTheme;
      el.style.removeProperty("background-color");
      el.style.removeProperty("background");
      el.style.removeProperty("border-color");
      el.style.removeProperty("color");
    }
  });
}

export function hexToRgb(hex) {
  if (!hex || typeof hex !== "string") return "66, 99, 235";
  let c = hex.replace("#", "").trim();
  if (c.length === 3) {
    c = c.split("").map((x) => x + x).join("");
  }
  const num = parseInt(c, 16);
  if (isNaN(num)) return "66, 99, 235";
  const r = (num >> 16) & 255;
  const g = (num >> 8) & 255;
  const b = num & 255;
  return `${r}, ${g}, ${b}`;
}

/**
 * Apply the theme color to Sidebar AND Buttons
 */
export function applyThemeBtnColor(color) {
  if (typeof document === "undefined") return;

  const activeColor = color || getSavedThemeBtnColor();
  const sidebarGradient = getSidebarGradient(activeColor);
  const rgb = hexToRgb(activeColor);

  // Set CSS variables on root
  document.documentElement.style.setProperty("--theme-btn-color", activeColor);
  document.documentElement.style.setProperty("--theme-btn-color-rgb", rgb);
  document.documentElement.style.setProperty("--theme-sidebar-bg", sidebarGradient);

  // Directly update sidebar element if mounted in DOM
  const sidebarEl = document.getElementById("sidebar");
  if (sidebarEl) {
    sidebarEl.style.setProperty("background", sidebarGradient, "important");
  }

  // Inject or update dynamic stylesheet for Sidebar and Target Buttons
  let styleEl = document.getElementById("dynamic-theme-style");
  if (!styleEl) {
    styleEl = document.createElement("style");
    styleEl.id = "dynamic-theme-style";
    document.head.appendChild(styleEl);
  }

  styleEl.textContent = `
    :root {
      --theme-btn-color: ${activeColor};
      --theme-btn-color-rgb: ${rgb};
      --theme-sidebar-bg: ${sidebarGradient};
    }
    input[type="checkbox"],
    input[type="radio"],
    .form-check-input {
      accent-color: ${activeColor} !important;
      cursor: pointer;
    }
    input[type="checkbox"]:checked,
    input[type="radio"]:checked,
    .form-check-input:checked {
      accent-color: ${activeColor} !important;
      background-color: ${activeColor} !important;
      border-color: ${activeColor} !important;
    }
    input[type="checkbox"]:focus,
    .form-check-input:focus {
      border-color: ${activeColor} !important;
      box-shadow: 0 0 0 0.2rem rgba(${rgb}, 0.25) !important;
    }
    .resend-action-icon,
    svg.resend-action-icon,
    .resend-icon,
    svg.resend-icon {
      color: ${activeColor} !important;
      fill: ${activeColor} !important;
    }
    .nav-tabs .nav-link {
      color: #64748b !important;
      font-weight: 500;
      border-top: 3px solid transparent !important;
      transition: color 0.15s ease, border-color 0.15s ease;
    }
    .nav-tabs .nav-link:hover {
      color: ${activeColor} !important;
    }
    .nav-tabs .nav-link.active,
    .nav-tabs .nav-item.show .nav-link {
      color: ${activeColor} !important;
      font-weight: 600 !important;
      background-color: #ffffff !important;
      border-color: #dee2e6 #dee2e6 #ffffff !important;
      border-top: 3px solid ${activeColor} !important;
    }
    #dashboard .nav-pills .nav-link.active,
    #dashboard .nav-pills .show > .nav-link,
    #dateRange .nav-pills .nav-link.active,
    #dateRange .nav-pills .show > .nav-link {
      background-color: ${activeColor} !important;
      color: #ffffff !important;
    }
    #dashboard .nav-pills .nav-link:hover,
    #dateRange .nav-pills .nav-link:hover {
      color: ${activeColor} !important;
    }
    .bg-theme-lavendar {
      background: ${sidebarGradient} !important;
    }
    .react-select__option--is-selected {
      background-color: ${activeColor} !important;
      color: #ffffff !important;
    }
    .react-select__option--is-focused:not(.react-select__option--is-selected) {
      background-color: rgba(${rgb}, 0.15) !important;
      color: #151940 !important;
    }
    #sidebar,
    nav#sidebar,
    .sidebar#sidebar {
      background: ${sidebarGradient} !important;
      transition: background 0.3s ease !important;
    }
    #sidebar .active,
    #sidebar .nav-link.active,
    #sidebar .nav-pills .nav-link.active {
      background: #ffffff !important;
      background-color: #ffffff !important;
      border-radius: 8px !important;
      color: ${activeColor} !important;
      font-weight: 600 !important;
    }
    #sidebar .active svg,
    #sidebar .active .sidebar-icon,
    #sidebar .active .sidebar-sub-icon,
    #sidebar .nav-link.active svg,
    #sidebar .nav-link.active .sidebar-icon,
    #sidebar .nav-link.active .sidebar-sub-icon {
      color: ${activeColor} !important;
      stroke: ${activeColor} !important;
    }
    #sidebar a img {
      filter: brightness(0) invert(1) !important;
      transition: filter 0.2s ease, opacity 0.2s ease !important;
    }
    #sidebar .active img {
      filter: brightness(0) !important;
      opacity: 0.8 !important;
    }
    .btn-primary,
    button.btn-primary,
    a.btn-primary,
    [data-dynamic-theme="true"],
    button[data-dynamic-theme="true"],
    a[data-dynamic-theme="true"],
    #createNew,
    button#createNew,
    #sidebar-logout-confirm-btn,
    #ledger .btn-primary,
    #reports .btn-primary {
      background-color: ${activeColor} !important;
      background: ${activeColor} !important;
      border-color: ${activeColor} !important;
      color: #ffffff !important;
      transition: filter 0.15s ease, background-color 0.2s ease !important;
    }
    .btn-primary:hover,
    button.btn-primary:hover,
    a.btn-primary:hover,
    [data-dynamic-theme="true"]:hover,
    button[data-dynamic-theme="true"]:hover,
    a[data-dynamic-theme="true"]:hover,
    #createNew:hover,
    #sidebar-logout-confirm-btn:hover,
    #ledger .btn-primary:hover,
    #reports .btn-primary:hover {
      filter: brightness(0.9) !important;
      color: #ffffff !important;
      background-color: ${activeColor} !important;
      border-color: ${activeColor} !important;
    }
    .btn-primary:active,
    button.btn-primary:active,
    a.btn-primary:active,
    [data-dynamic-theme="true"]:active,
    button[data-dynamic-theme="true"]:active,
    a[data-dynamic-theme="true"]:active,
    #createNew:active,
    #sidebar-logout-confirm-btn:active,
    #ledger .btn-primary:active,
    #reports .btn-primary:active {
      filter: brightness(0.8) !important;
    }
    .btn-primary:focus-visible,
    [data-dynamic-theme="true"]:focus-visible,
    #ledger .btn-primary:focus-visible,
    #reports .btn-primary:focus-visible {
      outline: 2px solid ${activeColor} !important;
      outline-offset: 2px !important;
    }
    #ledger .btn-outline-primary,
    #reports .btn-outline-primary {
      outline: 1px solid ${activeColor} !important;
      background: transparent !important;
      color: ${activeColor} !important;
      border-color: ${activeColor} !important;
    }
    #paginate .page-item.active .page-link,
    .pagination .page-item.active .page-link {
      background-color: ${activeColor} !important;
      background: ${activeColor} !important;
      border-color: ${activeColor} !important;
      color: #ffffff !important;
    }
    #paginate .page-link,
    .pagination .page-link {
      color: ${activeColor} !important;
    }
    #paginate .page-link:hover,
    .pagination .page-link:hover {
      color: ${activeColor} !important;
      background-color: rgba(0, 0, 0, 0.05) !important;
    }
    #paginate .page-item.disabled .page-link,
    .pagination .page-item.disabled .page-link {
      color: #6c757d !important;
      opacity: 0.6 !important;
    }
  `;


  scanAndApplyTargetButtons(activeColor);
}

let isObserverInitialized = false;

/**
 * Initialize DOM observer to keep buttons & sidebar styled on navigation and state updates
 */
export function initThemeObserver() {
  if (typeof window === "undefined" || typeof document === "undefined") return;

  // Run initial theme application
  const currentColor = getSavedThemeBtnColor();
  applyThemeBtnColor(currentColor);

  if (isObserverInitialized) return;
  isObserverInitialized = true;

  // Throttled scan on DOM mutations
  let scheduled = false;
  const observer = new MutationObserver(() => {
    if (!scheduled) {
      scheduled = true;
      requestAnimationFrame(() => {
        scheduled = false;
        applyThemeBtnColor();
      });
    }
  });

  observer.observe(document.body || document.documentElement, {
    childList: true,
    subtree: true,
    characterData: true,
  });

  // Listen to storage events from other tabs
  window.addEventListener("storage", (e) => {
    if (e.key === THEME_BTN_STORAGE_KEY) {
      applyThemeBtnColor(e.newValue || DEFAULT_THEME_BTN_COLOR);
    }
  });

  // Fetch and sync user theme from server if logged in
  fetchAndApplyUserTheme();
}

/**
 * Safely extract theme_color from API response
 */
export function extractThemeColorFromProfile(response) {
  if (!response) return null;
  const data = response?.data?.data || response?.data || response;

  if (typeof data === "string" && /^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/.test(data)) {
    return data;
  }

  if (data?.theme_color && /^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/.test(data.theme_color)) {
    return data.theme_color;
  }

  if (Array.isArray(data?.list) && data.list.length > 0) {
    const col = data.list[0]?.theme_color;
    if (col && /^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/.test(col)) {
      return col;
    }
  }

  if (Array.isArray(data) && data.length > 0) {
    const col = data[0]?.theme_color;
    if (col && /^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/.test(col)) {
      return col;
    }
  }

  if (data?.user?.theme_color && /^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/.test(data.user.theme_color)) {
    return data.user.theme_color;
  }

  if (data?.userData?.theme_color && /^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/.test(data.userData.theme_color)) {
    return data.userData.theme_color;
  }

  return null;
}

/**
 * Fetch theme_color from server API /user/listUserProfile and apply it
 */
export async function fetchAndApplyUserTheme(userId) {
  try {
    const inv = JSON.parse(localStorage.getItem("inventory") || "{}");
    const uid = userId || inv?.userData?.user_id;
    const token = inv?.token;
    if (!uid || !token) return null;

    const res = await apiListUserProfile({ user_id: uid });
    const serverColor = extractThemeColorFromProfile(res);

    if (serverColor) {
      saveThemeBtnColor(serverColor);
      if (inv?.userData) {
        inv.userData.theme_color = serverColor;
        localStorage.setItem("inventory", JSON.stringify(inv));
      }
      return serverColor;
    }
  } catch (err) {
    console.warn("fetchAndApplyUserTheme error:", err?.message || err);
  }
  return null;
}

